import { useCallback, useEffect, useRef, useState } from "react";

import { useDispatch } from "react-redux";
import { updateOrders } from "../../redux/notificationsSlice";

import { loadUser } from "../../utils/AuthDataUtils";
import { loadOrdersWithUsers } from "../../utils/AxiosUtils";
import {
  datetimeStringToStringAgo,
  formatPhoneString,
  labelToPriority,
  parseOrderStatus,
  inlineReturn,
} from "../../utils/StringUtils";

import { Col, Container, Row } from "reactstrap";

import CustomTable from "../../components/CustomTable";
import CustomOrderDetails from "../../components/CustomOrderDetails";
import CustomText from "../../components/CustomText";

import { FaRegCommentAlt, FaRegComments } from "react-icons/fa";

import Colors from "../../constants/Colors";

import { RELOAD_TIME_MS } from "../../constants/Paramters";

export default function Dashboard() {
  const user = loadUser();
  console.log("Render Dashboard, user:", user !== null);

  const dispatch = useDispatch();

  const clearTimerRef = useRef();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allSolved, setAllSolved] = useState(false);
  const [id, setId] = useState();
  const [refreshedAt, setRefreshedAt] = useState();

  const sortOrders = (orders) => {
    return orders.sort((a, b) => {
      const prioA = labelToPriority(a.clientData?.label);
      const prioB = labelToPriority(b.clientData?.label);
      return prioA > prioB || new Date(a.createdAt) - new Date(b.createdAt);
    });
  };

  const reload = useCallback(() => {
    console.log("Dashboard reload");
    const querry = "page=0&status=NEW,PROCESSING";
    loadOrdersWithUsers(querry)
      .then((page) => {
        const sorted = sortOrders(page.content);
        setOrders(sorted);
        setAllSolved(sorted.length === 0);
        dispatch(updateOrders());
        setId((oldId) => (!oldId && sorted.length ? sorted[0].id : oldId));
      })
      .catch((err) => console.warn(err))
      .finally(() => {
        setLoading(false);
        clearTimeout(clearTimerRef.current);
        clearTimerRef.current = setTimeout(reload, RELOAD_TIME_MS);
      });
  }, [dispatch]);

  useEffect(() => {
    reload();
    return () => clearTimeout(clearTimerRef.current);
  }, [reload]);

  const onRowClick = (index) => {
    console.log("Dashboard onRowClick", index);
    setId(orders[index].id);
    setRefreshedAt(new Date());
  };

  const onUpdate = (newOrder) => {
    console.log("Orders onUpdate", newOrder);
    reload();
  };

  const renderClient = (order) => {
    const client = order.clientData;
    if (client) {
      return client.name;
    }
    return formatPhoneString(order.client);
  };

  const renderSeller = (order) => {
    if (!order.seller) {
      return null;
    }
    const seller = order.sellerData;
    if (seller) {
      return seller.name;
    }
    return formatPhoneString(order.seller);
  };

  const renderStatus = (status, text) => {
    let color = "success";
    if (status === "NEW") color = "secondary";
    if (status === "PROCESSING") color = "primary";
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span className={`badge bg-${color} px-2 py-1 fs-7`}>{text}</span>
      </div>
    );
  };

  const isSelected = (item) => {
    return item.id === id;
  };

  const renderChatStatus = (order) => {
    const isComment = order?.comments.length > 0;
    const isMessage = order?.messages.length > 0;
    const isNewComment =
      order?.id === id
        ? false
        : order?.comments?.some(
            (comment) => !comment.seenBy?.includes(user.phone)
          ) || false;
    const isNewMessage =
      order?.id === id
        ? false
        : order?.messages?.some(
            (message) => !message.seenBy?.includes(user.phone)
          ) || false;

    const pulseAnimation = {
      animation: "pulse 0.7s infinite",
      color: "#ff4444",
    };

    const normalStyle = {
      color: "#666666",
    };

    return (
      <>
        <style>
          {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.4; }
            100% { opacity: 1; }
          }
        `}
        </style>
        {isComment ? (
          <FaRegCommentAlt
            style={isNewComment ? pulseAnimation : normalStyle}
          />
        ) : (
          <FaRegCommentAlt style={{ color: "transparent" }} />
        )}
        {isMessage && (
          <FaRegComments
            style={{
              marginLeft: "4px",
              ...(isNewMessage ? pulseAnimation : normalStyle),
            }}
          />
        )}
      </>
    );
  };

  const tableLayout = [
    { name: "Nr", key: (o) => inlineReturn(o.number), width: "5%" },
    {
      name: " ",
      key: (o) => renderStatus(o.status, parseOrderStatus(o.status)),
      width: "5%",
    },
    { name: "Klient", key: (o) => renderClient(o), width: "30%" },
    { name: "Pracownik", key: (o) => renderSeller(o), width: "30%" },
    {
      name: "Utworzone",
      key: (o) => datetimeStringToStringAgo(o.createdAt),
      width: "20%",
    },
    {
      name: " ",
      key: (o) => renderChatStatus(o),
      width: "10%",
    },
  ];

  const tableOrEmpty = allSolved ? (
    <Container
      fluid
      className="d-flex flex-column justify-content-center align-items-center"
      style={{ height: "70vh" }}
    >
      <img
        src="https://media.giphy.com/media/5b7mDzTcVN3IblMwZI/giphy.gif"
        alt="Piweczko"
        style={{ width: "250px", maxWidth: "90%", borderRadius: "1rem" }}
      />
      <CustomText size={24} color={Colors.quaternary}>
        Wszystkie zamówienia rozpykane!
      </CustomText>
    </Container>
  ) : (
    <Row className="mt-3">
      <CustomTable
        loading={loading}
        data={orders}
        layout={tableLayout}
        onRowClick={onRowClick}
        isSelected={isSelected}
      />
    </Row>
  );

  return (
    <Col sm={{ size: 10, offset: 1 }} md={{ size: 10, offset: 1 }}>
      <Row>
        <Col sm={6}>{tableOrEmpty}</Col>
        <Col sm={6}>
          <CustomOrderDetails
            id={id}
            refreshedAt={refreshedAt}
            onUpdate={onUpdate}
          />
        </Col>
      </Row>
    </Col>
  );
}
